import {
  Autocomplete,
  TextField,
  createFilterOptions,
} from '@iclinic/design-system';
import { useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'shared/hooks';
import { getAutocomplete } from 'features/tissInvoicing/state/autocomplete/selectors';
import { AppointmentGuideFormValues } from 'features/tissInvoicing/types/appointmentGuide';
import { FilterOptionsState } from 'features/records/highlightsInfo/types';
import { actions as autocompleteActions } from 'features/tissInvoicing/state/autocomplete';
import { ProcedureAutocompleteEntry } from 'features/tissInvoicing/types';

export default ({
  label = '21 - Código do procedimento',
  required = false,
}: {
  label?: string;
  required?: boolean;
}) => {
  const dispatch = useDispatch();

  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext<AppointmentGuideFormValues>();

  const procedureFilter = createFilterOptions<ProcedureAutocompleteEntry>();
  const { procedures } = useSelector(getAutocomplete);

  const getOptionLabelFn = useCallback(
    <OptionType extends { inputValue?: string; search_name?: string | null }>(
      option: OptionType,
    ) => {
      if (typeof option === 'string') return option;

      if (option?.inputValue) return option?.inputValue;

      return option?.search_name || '';
    },
    [],
  );

  const renderOptionFn = useCallback(
    <OptionType extends { inputValue?: string; search_name?: string | null }>(
      option: OptionType,
    ) => {
      if (option?.inputValue) return `"${option?.inputValue}"`;

      return option?.search_name || '';
    },
    [],
  );

  const handleChange = useCallback((_, value) => {
    const typedValue = value as ProcedureAutocompleteEntry;

    setFieldValue('procedure_code', typedValue?.code || typedValue || '');
  }, []);

  const handleFetchProceduresOptions = debounce((inputValue: string): void => {
    dispatch(
      autocompleteActions.fetchProceduresOptions({
        tableCode: values.tuss_table_code,
        searchName: inputValue,
      }),
    );
  }, 500);

  const handleInputChange = (event: any) => {
    if (event) {
      const inputValue = event.target.value;

      if ((!inputValue || inputValue === '') && values.tuss_table_code) {
        handleFetchProceduresOptions('');
        return;
      }

      if (inputValue.length >= 3 && values.tuss_table_code) {
        handleFetchProceduresOptions(inputValue);
      }
    }
  };

  const handleOpen = () => {
    if (values.tuss_table_code) {
      dispatch(
        autocompleteActions.fetchProceduresOptions({
          tableCode: values.tuss_table_code,
        }),
      );
    }
  };

  const filterOptions = (
    options: ProcedureAutocompleteEntry[],
    params: FilterOptionsState<ProcedureAutocompleteEntry>,
  ) => procedureFilter(options, params);

  return (
    <Autocomplete
      id="procedure_code"
      freeSolo
      fullWidth
      options={procedures}
      value={values.procedure_code}
      clearOnBlur
      onBlur={handleBlur}
      onChange={handleChange}
      getOptionLabel={getOptionLabelFn}
      renderOption={renderOptionFn}
      filterOptions={filterOptions}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name="procedure_code"
          data-ga="procedure_code"
          required={required}
          error={!!errors.procedure_code}
          helperText={touched.procedure_code && errors.procedure_code}
        />
      )}
    />
  );
};
